import { min, max } from 'd3-array';

import { averageTypes } from '../enums/indicators/mae';
import Indicator from '../Indicator';
import LineMarker from '../markers/LineMarker';

/** MAE indicator */
export default class MAEIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} componentSeries - Indicator component series
	 */
	_getDefaultParams() {
		return {
			id: 'mae',
			name: 'Moving Average Envelope',
			description:
				"Moving Average Envelopes provide context around a security's recent price range.",
			inputs: [
				{ name: 'period', value: 20 },
				{ name: 'lowfactor', value: 0.95, max: 10 },
				{ name: 'highfactor', value: 1.05, max: 10 },
				{ name: 'averagetype', value: averageTypes.SMA }
			],
			componentSeries: [{ id: 'maelow' }, { id: 'maehigh' }]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return min(data.mae || [], i => +i.maelow);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return max(data.mae || [], i => +i.maehigh);
	}

	/**
	 * @returns {object}
	 * @property {LineMarker} lineUp
	 * @property {LineMarker} lineDown
	 * @property {LineMarker} lineFill
	 */
	_getMarkers() {
		return (
			this.markers || {
				lineUp: new LineMarker(),
				lineDown: new LineMarker(),
				lineFill: new LineMarker()
			}
		);
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		const ctx = this.panel.rootContext;
		const dataUp = this._getDataMap(data.mae, 'maehigh');
		const dataDown = this._getDataMap(data.mae, 'maelow');

		this.markers.lineFill.renderFillBetween(
			ctx,
			dataUp,
			dataDown,
			this.xScale,
			this.yScale,
			this.params.style.background.color
		);

		this._renderLineMarker({
			dataMap: dataUp,
			sessionType,
			marker: this.markers.lineUp
		});

		this._renderLineMarker({
			dataMap: dataDown,
			sessionType,
			marker: this.markers.lineDown
		});
	}
}
